import React, { useContext, useState, forwardRef } from "react";
import firebase from "firebase/compat/app";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Button,
  Link,
  Alert,
  Slide,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import { CloseRounded, QrCode2, LocalHospital, OpenInNew } from "@mui/icons-material";
import { AuthContext } from "@healthdesk/shared-hooks";
import { db, getSpecialtyLabel } from "@healthdesk/shared-services";
import { formatDistanceToNow } from "date-fns";

import { getFacilityTypeLabel } from "../../constants/facilityTypes";
import StarRating from "./Modal/StarRating";
import Loading from "../Loading";
import { getAccessRestrictionLabel } from "./mapEdDisplay";
import SeatData from "./SeatData";
import QueueForm from "../queue/QueueForm";
import { getActiveWaitTime } from "@healthdesk/shared-utils";
import CapabilityBreakdownPanel from "./CapabilityBreakdownPanel";
import EdClaimPanel, { canShowEdClaimPanel } from "./EdClaimPanel";
import EdDataFlagPanel, { canShowEdDataFlagPanel } from "./EdDataFlagPanel";
import EdClaimChallengePanel, { canShowEdClaimChallengePanel } from "./EdClaimChallengePanel";

const Transition = forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function to get current occupancy state description
const getWaitingStateText = (waitTime) => {
  if (!waitTime) return "Status unknown";

  const waitTimeNum =
    typeof waitTime === "string" ? parseInt(waitTime, 10) : waitTime;

  if (waitTimeNum <= 30) return "Currently empty";
  if (waitTimeNum <= 120) return "Currently half-full";
  if (waitTimeNum <= 240) return "Currently full";
  return "Currently overflowing";
};

const Modal = ({
  modalOpen,
  modalVisible,
  setModalVisible,
  matchData,
  isEmbedded = false,
}) => {
  const { user } = useContext(AuthContext);
  const isAuthenticated = !!user && !user.isAnonymous;
  const [messageSent, setMessageSent] = useState(false);

  const canClaimEd = canShowEdClaimPanel(modalOpen, Boolean(user));
  const canReportData = canShowEdDataFlagPanel(modalOpen, Boolean(user));
  const canChallengeClaim = canShowEdClaimChallengePanel(modalOpen, Boolean(user));

  // Get the active wait time using the same function as other components
  const activeWaitTime = modalOpen?.waitTimes
    ? getActiveWaitTime(modalOpen)
    : null;

  // Use activeWaitTime for consistency with other components, fall back to averageWaitTime or waitScore
  const waitTimeToDisplay =
    activeWaitTime?.waitTime !== undefined
      ? activeWaitTime.waitTime
      : modalOpen?.averageWaitTime !== undefined
      ? modalOpen.averageWaitTime
      : modalOpen?.waitScore;

  const hasTime = waitTimeToDisplay !== undefined;
  const locationName = modalOpen?.title;
  const locationRef = modalOpen?.id?.toString();
  const accessRestrictionLabel = getAccessRestrictionLabel(modalOpen?.accessRestriction);

  const capitalize = (string) => {
    if (!string) return "";
    return string
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
      .join(" ");
  };

  // Get a human-readable time ago string
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return null;

    try {
      const d = new Date(timestamp);
      return (
        <span title={d.toISOString()}>
          {formatDistanceToNow(d, { addSuffix: true })}
        </span>
      );
    } catch (error) {
      console.error("Error formatting time ago:", error);
      return null;
    }
  };

  const sendMessage = async (number) => {
    setMessageSent("loading");
    try {
      await db.collection("messages").add({
        to: number,
        body: `A user requested to know the current state of the waiting room at your facility. You can update your facility's estimate by signing in at hlthdsk.com.`,
      });
      setMessageSent("sent");
    } catch (error) {
      console.error("Error sending message:", error);
      setMessageSent("error");
    }
  };

  return (
    <Dialog
      open={modalVisible}
      onClose={() => setModalVisible(false)}
      maxWidth="md"
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          position: "relative",
          p: 2,
          m: 2,
          minHeight: "200px",
          width: "100%",
          maxWidth: {
            xs: "420px", // Default/mobile width
            sm: "600px", // Tablet and up
            md: "800px", // Desktop and up
          },
          borderRadius: "12px",
        },
      }}
      sx={{
        "& .MuiDialog-paper": {
          margin: 0,
          position: "fixed",
          bottom: 0,
          mx: "auto",
          left: "50%",
          transform: "translateX(-50%)",
        },
      }}
    >
      <IconButton
        onClick={() => setModalVisible(false)}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          zIndex: 1,
        }}
      >
        <CloseRounded />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ textAlign: "center", mt: 2, mb: 2 }}>
          {modalOpen?.title && (
            <Typography
              variant="h5"
              color="primary"
              sx={{
                fontWeight: 500,
                mb: 1,
                fontSize: {
                  xs: "1.15rem",
                  sm: "1.3rem",
                  md: "1.5rem",
                },
              }}
            >
              {capitalize(modalOpen.title)}
            </Typography>
          )}

          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: {
                xs: "0.875rem",
                sm: "1rem",
              },
            }}
          >
            {getFacilityTypeLabel(modalOpen)}
          </Typography>

          {modalOpen?.pediatricOnly ? (
            <Chip
              label="Children's hospital"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mb: 0.75 }}
            />
          ) : null}
          {accessRestrictionLabel ? (
            <Chip
              label={accessRestrictionLabel}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ mb: 1, display: "block", width: "fit-content" }}
            />
          ) : null}

          {modalOpen?.rating ? <StarRating rating={modalOpen.rating} /> : null}

          <Typography
            variant="subtitle1"
            color="primary"
            sx={{
              fontWeight: 500,
              mt: 2,
              mb: 1,
              fontSize: {
                xs: "0.9rem",
                sm: "1rem",
              },
            }}
          >
            {activeWaitTime?.waitTime === undefined
              ? "Anticipated Waiting Room Experience"
              : "Waiting Time Estimate"}
          </Typography>

          {hasTime ? (
            <SeatData waitTime={waitTimeToDisplay} hideStatus={true} />
          ) : null}

          {!hasTime ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                fontSize: {
                  xs: "0.75rem",
                  sm: "0.875rem",
                },
              }}
            >
              No current estimated waiting room time.
            </Typography>
          ) : null}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mb: 1,
              fontSize: {
                xs: "0.7rem",
                sm: "0.75rem",
              },
            }}
          >
            {activeWaitTime?.date ? (
              <>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {getWaitingStateText(waitTimeToDisplay)}
                </Box>
                <Box component="span" sx={{ mx: 0.3 }}>
                  •
                </Box>
                <Box component="span">
                  updated {getTimeAgo(activeWaitTime.date)}
                </Box>
              </>
            ) : modalOpen?.lastUpdated ? (
              <>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {waitTimeToDisplay
                    ? getWaitingStateText(waitTimeToDisplay)
                    : "Status unknown"}
                </Box>
                <Box component="span" sx={{ mx: 0.3 }}>
                  •
                </Box>
                <Box component="span">
                  updated {getTimeAgo(modalOpen.lastUpdated)}
                </Box>
              </>
            ) : hasTime ? (
              <>
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {getWaitingStateText(waitTimeToDisplay)}
                </Box>
                <Box component="span" sx={{ mx: 0.3 }}>
                  •
                </Box>
                <Box component="span">updated March 8, 2024*</Box>
                {!user && (
                  <Link
                    href="/register"
                    sx={{
                      ml: 1,
                      color: "primary.main",
                      fontSize: {
                        xs: "0.75rem",
                        sm: "0.875rem",
                      },
                    }}
                  >
                    Claim this Facility
                  </Link>
                )}
              </>
            ) : null}
          </Typography>
        </Box>

        {(isAuthenticated || isEmbedded) ? (
          <CapabilityBreakdownPanel matchData={matchData} location={modalOpen} />
        ) : null}

        {/* Check-in Links Section - Shows if facility has check-in enabled clinics */}
        {modalOpen?.checkInClinics && modalOpen.checkInClinics.length > 0 && (
          <Paper 
            variant="outlined" 
            sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'primary.50',
              borderColor: 'primary.200',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <QrCode2 color="primary" />
              <Typography variant="subtitle2" color="primary" fontWeight={600}>
                Online Check-in Available
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Skip the waiting room by checking in online before you arrive:
            </Typography>
            
            <List dense sx={{ p: 0 }}>
              {modalOpen.checkInClinics.map((clinic, index) => (
                <ListItem 
                  key={clinic.clinicId || index}
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <ListItemButton
                    component="a"
                    href={clinic.checkInUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalHospital fontSize="small" color="primary" />
                          <Typography variant="body2" fontWeight={500}>
                            {clinic.clinicName}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {clinic.specialties && clinic.specialties.map((specialty) => (
                            <Chip
                              key={specialty}
                              label={getSpecialtyLabel(specialty)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          ))}
                        </Box>
                      }
                    />
                    <OpenInNew fontSize="small" color="action" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        <QueueForm
          queue={modalOpen?.queue}
          queueEnabled={modalOpen?.queueEnabled}
          queueNumber={modalOpen?.queueNumber}
          queueCap={modalOpen?.queueCap}
          queueLength={modalOpen?.queueLength}
          locationName={locationName}
          locationRef={locationRef}
          firebase={firebase}
          db={db}
          textSequence={modalOpen?.textSequence}
        />

        {modalOpen?.phone && !modalOpen?.queueEnabled && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            {messageSent === "sent" ? (
              <Alert severity="success">
                Thank you. A message has been sent.
              </Alert>
            ) : (
              <Button
                variant="outlined"
                onClick={() => sendMessage(modalOpen.phone)}
                disabled={messageSent === "loading"}
              >
                {messageSent === "loading" ? (
                  <Loading />
                ) : (
                  "Request Current WR Update"
                )}
              </Button>
            )}
          </Box>
        )}

        {canClaimEd ? (
          <EdClaimPanel location={modalOpen} locationId={locationRef} />
        ) : null}

        {canReportData ? (
          <EdDataFlagPanel location={modalOpen} locationId={locationRef} />
        ) : null}

        {canChallengeClaim ? (
          <EdClaimChallengePanel location={modalOpen} locationId={locationRef} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
